// Rivals: the one thing to do now (your turn, or play, or challenge someone), today's edition with
// everyone you're up against, the rest of the week a row a day, and your record with each rival.
import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { Challenge, EditionInfo, Play, Rival } from '@wbwl/contract'
import { ChallengeSheet } from '../components/challenge-ui.tsx'
import { Icon } from '../components/Icon.tsx'
import { DayCard, RivalRow, TurnCard, WeekRow } from '../components/rivals-ui.tsx'
import { ErrorNote, Loading, TabBar, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useEdition, useMe, useMyChallenges, usePlay, useRivals } from '../lib/data.ts'
import { fmt, weekday } from '../lib/game.ts'
import { dayOf, type Day } from '../lib/rivals.ts'
import { invalidate, setResource } from '../lib/store.ts'

export default function RivalsPage() {
  const me = useMe().data
  const ed = useEdition('today')
  const info = ed.data
  const play = usePlay(info?.number ?? null).data
  const mine = useMyChallenges()
  const rivalsQ = useRivals()
  const [sheet, setSheet] = useState<Challenge | null>(null)
  const navigate = useNavigate()
  const toast = useToast()

  const error = ed.error ?? mine.error ?? rivalsQ.error
  if (error) return <div className="page"><ErrorNote error={error} retry={() => { void ed.reload(); void mine.reload(); void rivalsQ.reload() }} /></div>
  if (!info || !me || !mine.data || !rivalsQ.data) return <div className="page"><Loading label="Finding your rivals…" /></div>

  const rivals = rivalsQ.data.rivals
  const days = mine.data.editions.map(dayOf)
  const turns = days.filter((d) => d.yourTurn)
  const today = days.find((d) => d.edition === info.number && d.me?.done) ?? null
  const past = days.filter((d) => !d.yourTurn && d !== today && d.me?.done)
  const fresh = (d: Day) => invalidate('mychallenges', `edchallenges:${d.edition}`, 'rivals')

  async function playDay(d: Day) {
    // Playing answers the invites: it counts you in.
    await Promise.all(d.invites.map((c) => api.post(`/challenges/${c.code}/join`).catch(() => {})))
    fresh(d)
    navigate(d.edition === info!.number ? '/play' : `/play/${d.edition}`)
  }
  async function accept(d: Day) {
    await Promise.all(d.toAccept.map((c) => api.post(`/challenges/${c.code}/join`).catch(() => {})))
    fresh(d)
    navigate(`/c/${(d.open ?? d.toAccept[0]).code}`)
  }
  async function skip(d: Day) {
    await Promise.all(d.invites.map((c) => api.post(`/challenges/${c.code}/decline`).catch(() => {})))
    fresh(d)
  }
  async function invite(edition: number, own: Challenge | null) {
    if (own) return setSheet(own)
    try {
      const ch = await api.post<Challenge>('/challenges', { edition })
      setResource(`challenge:${ch.code}`, ch)
      invalidate('mychallenges', `edchallenges:${edition}`)
      setSheet(ch)
    } catch (e) {
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t make a challenge. Try again.')
    }
  }

  const sum = (pick: (r: Rival) => number) => rivals.reduce((s, r) => s + pick(r), 0)
  const empty = rivals.length === 0 && days.length === 0

  return (
    <div className="page rivals">
      <header className="r-head">
        <h1 className="h-serif">Rivals</h1>
        <div className="sub">
          {rivals.length
            ? <><b>{sum((r) => r.record.wins)}</b> won · <b>{sum((r) => r.record.losses)}</b> lost · <b>{sum((r) => r.record.ties)}</b> tied, against {rivals.length} player{rivals.length === 1 ? '' : 's'}</>
            : 'Friends you play a challenge with show up here, with your record against each.'}
        </div>
      </header>

      <div className="rivals-cols">
        <div>
          {empty ? <FirstVisit info={info} play={play} onChallenge={() => invite(info.number, null)} /> : (
            <>
              {turns.length > 0 && (
                <>
                  <div className="sec-head rv-sec"><h3 className="turn">Your turn</h3></div>
                  <div className="rv-stack">
                    {turns.map((d) => <TurnCard key={d.edition} day={d} today={d.edition === info.number} onPlay={() => playDay(d)} onSkip={d.invites.length && !d.challenges.some((c) => c.me === 'joined') ? () => skip(d) : undefined} />)}
                  </div>
                </>
              )}
              {today && (
                <>
                  <div className="sec-head rv-sec"><h3>Today</h3></div>
                  <DayCard day={today} title={`No. ${today.edition} · ${weekday(today.date)}`} onInvite={() => invite(today.edition, today.own)} onAccept={() => accept(today)} />
                </>
              )}
              {turns.length === 0 && !today && <Ask info={info} play={play} rivals={rivals} onChallenge={() => invite(info.number, null)} />}
              {past.length > 0 && (
                <>
                  <div className="sec-head rv-sec"><h3>This week</h3><span className="eyebrow">Open for 7 days</span></div>
                  <div className="card list">{past.map((d) => <WeekRow key={d.edition} day={d} />)}</div>
                </>
              )}
            </>
          )}
        </div>
        {rivals.length > 0 && (
          <div>
            <div className="sec-head rv-sec"><h3>Rivals</h3><span className="eyebrow">Latest first</span></div>
            <div className="card list">{rivals.map((r) => <RivalRow key={r.player.id} rival={r} />)}</div>
          </div>
        )}
      </div>

      <TabBar />
      <ChallengeSheet challenge={sheet} me={me} onClose={() => setSheet(null)} onChange={setSheet} />
    </div>
  )
}

/** Nothing waiting and no edition card: play today's, or once you have, challenge someone to it. */
function Ask({ info, play, rivals, onChallenge }: { info: EditionInfo; play: Play | undefined; rivals: readonly Rival[]; onChallenge(): void }) {
  const navigate = useNavigate()
  const done = play?.phase === 'done'
  const names = rivals.slice(0, 2).map((r) => r.player.displayName)
  return (
    <section className="card rc ask">
      <div className="ask-top">
        <span className="ask-icon"><Icon name="swords" size={20} /></span>
        <div>
          <b>{done ? `Think your rivals can beat ${fmt(play.scores.total)}?` : `Play No. ${info.number}, then challenge your rivals`}</b>
          <p>{done
            ? rivals.length ? `Send No. ${info.number} to ${names.join(rivals.length > 2 ? ', ' : ' and ')}${rivals.length > 2 ? ` and ${rivals.length - 2} other${rivals.length === 3 ? '' : 's'}` : ''}, or share a link with anyone.` : 'Send a link to one friend or a group chat.'
            : 'They get it right here, and you’ll see each other’s boards when you’re both done.'}</p>
        </div>
      </div>
      {done
        ? <button type="button" className="btn gold block" onClick={onChallenge}><Icon name="swords" />Challenge a friend</button>
        : <button type="button" className="btn ink block" onClick={() => navigate('/play')}>{play && play.rows.length > 0 ? `Continue No. ${info.number}` : `Play No. ${info.number}`}</button>}
    </section>
  )
}

/** No rivals and no challenges yet: how one is made. */
function FirstVisit({ info, play, onChallenge }: { info: EditionInfo; play: Play | undefined; onChallenge(): void }) {
  const navigate = useNavigate()
  const done = play?.phase === 'done'
  return (
    <section className="card rc">
      <div className="rc-head"><span className="eyebrow">How you get a rival</span><Icon name="swords" size={18} className="burn-ink" /></div>
      <ol className="rival-steps">
        <li><b>Finish today’s edition</b><p>{done ? `Done: ${fmt(play.scores.total)} on No. ${info.number}.` : `No. ${info.number} is waiting for you.`}</p></li>
        <li><b>Send the link</b><p>To one friend or a group chat. It stays open for 7 days, and they don’t need an account.</p></li>
        <li><b>Compare boards</b><p>Everyone who plays it becomes a rival, with a running record of who’s ahead.</p></li>
      </ol>
      <div className="rc-acts">
        {done
          ? <button type="button" className="btn gold block" onClick={onChallenge}><Icon name="swords" />Challenge a friend</button>
          : <button type="button" className="btn ink block" onClick={() => navigate('/play')}>Play No. {info.number}</button>}
      </div>
      <p className="rc-note">Once you have rivals, you can challenge them from here — no link needed.</p>
    </section>
  )
}
